// File: src/components/home/HomeNewsFeed.js
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs 
} from "firebase/firestore";
import { useProfile } from "../../context/ProfileContext";

// 🧠 IN-MEMORY SESSION CACHE (Lives outside component lifecycle)
// Preserves the fetched articles across route changes so 0 reads are made on return visits.
let sessionNewsPool = null;

// Utility: Fisher-Yates array shuffle for uniform randomness
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const HomeNewsFeed = ({ isDarkMode }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(!sessionNewsPool); // Only show spinner if memory is empty
  const [activeLayoutPattern, setActiveLayoutPattern] = useState("standard-6");

  const navigate = useNavigate();
  const { user } = useProfile();
  const db = getFirestore();

  useEffect(() => {
    let isMounted = true;

    // 1. Randomize layout variation on this visit
    const chosenLayout = Math.random() > 0.5 ? "merged-5" : "standard-6";
    setActiveLayoutPattern(chosenLayout);
    const targetCount = chosenLayout === "merged-5" ? 5 : 6;

    // Helper: Selects, deduplicates, and shuffles from a raw post pool
    const processAndSetPosts = (rawPosts) => {
      const spotlightPost = rawPosts.find(
        (p) => p.isSpotlight === true || p.isAdminAnnouncement === true
      );

      const nonSpotlight = rawPosts.filter(
        (p) => p.id !== spotlightPost?.id
      );

      const seenArtistIds = new Set();
      if (spotlightPost?.artistId) {
        seenArtistIds.add(spotlightPost.artistId);
      }

      const uniqueArtistPosts = [];
      for (const item of nonSpotlight) {
        const artistKey = item.artistId || item.id;
        if (!seenArtistIds.has(artistKey)) {
          seenArtistIds.add(artistKey);
          uniqueArtistPosts.push(item);
        }
      }

      // Fresh shuffle on every visit
      const randomizedOrganicPosts = shuffleArray(uniqueArtistPosts);

      const finalSelection = spotlightPost
        ? [spotlightPost, ...randomizedOrganicPosts].slice(0, targetCount)
        : randomizedOrganicPosts.slice(0, targetCount);

      if (isMounted) {
        setPosts(finalSelection);
        setLoading(false);
      }
    };

    // 2. CHECK IN-MEMORY CACHE FIRST (0 Reads!)
    if (sessionNewsPool && sessionNewsPool.length > 0) {
      processAndSetPosts(sessionNewsPool);
      return;
    }

    // 3. FETCH IF MEMORY IS EMPTY (Runs only on initial visit or hard browser refresh)
    const fetchNewsData = async () => {
      try {
        setLoading(true);
        let rawPosts = [];

        // Attempt Single Summary Doc
        try {
          const globalCacheRef = doc(db, "system_caches/homeNewsFeed");
          const globalSnap = await getDoc(globalCacheRef);
          if (globalSnap.exists() && Array.isArray(globalSnap.data().posts)) {
            rawPosts = globalSnap.data().posts;
          }
        } catch (e) {
          // Ignore and continue to fallback query
        }

        // Fallback Query
        if (rawPosts.length === 0) {
          const postsRef = collection(db, "artistPosts");
          const q = query(
            postsRef,
            where("deletedAt", "==", null),
            orderBy("createdAt", "desc"),
            limit(20)
          );
          const snap = await getDocs(q);
          rawPosts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }

        // Save pool to session memory
        sessionNewsPool = rawPosts;

        processAndSetPosts(rawPosts);
      } catch (err) {
        console.error("Error loading homepage news:", err);
        if (isMounted) setLoading(false);
      }
    };

    fetchNewsData();

    return () => {
      isMounted = false;
    };
  }, [db, user?.uid]);

  // Fallback items if collection has fewer items than target count
  const defaultPlaceholders = useMemo(
    () => [
      {
        id: "placeholder-1",
        title: "Australian Indie Music Explodes Across Syndicated Airwaves",
        atomifiedTitle: "Australian Indie Music Explodes Across Syndicated Airwaves",
        atomifiedText: "Over 50 community stations synchronize to broadcast independent local acts.",
        mediaUrl: null,
        isSpotlight: true,
      },
      {
        id: "placeholder-2",
        title: "Square Tugs Drop High-Octane Punk Anthem",
        atomifiedTitle: "Square Tugs Drop High-Octane Punk Anthem",
        atomifiedText: "Fast, loud, and uncompromised garage rock hitting local airwaves.",
        mediaUrl: null,
      },
      {
        id: "placeholder-3",
        title: "Matt Lane Unveils 'Pale Blue Dot'",
        atomifiedTitle: "Matt Lane Unveils 'Pale Blue Dot'",
        atomifiedText: "An acoustic reflection after a two-year hiatus.",
        mediaUrl: null,
      },
      {
        id: "placeholder-4",
        title: "Spyrious Debut Live Adelaide Residency",
        atomifiedTitle: "Spyrious Debut Live Adelaide Residency",
        atomifiedText: "Post-rock veterans announce a run of regional venue showcases.",
        mediaUrl: null,
      },
      {
        id: "placeholder-5",
        title: "Carnival Road Live Tour Dates",
        atomifiedTitle: "Carnival Road Live Tour Dates",
        atomifiedText: "Canberra-born alt-rock collective hit the road this month.",
        mediaUrl: null,
      },
      {
        id: "placeholder-6",
        title: "Wings of Knives Debut Release",
        atomifiedTitle: "Wings of Knives Debut Release",
        atomifiedText: "Heavy distortion and melodic hooks converge on new studio recording.",
        mediaUrl: null,
      },
    ],
    []
  );

  const targetCount = activeLayoutPattern === "merged-5" ? 5 : 6;
  const displayItems = (
    posts.length >= targetCount
      ? posts
      : [...posts, ...defaultPlaceholders.slice(posts.length)]
  ).slice(0, targetCount);

  const getTileClasses = (index) => {
    if (index === 0) return "md:col-span-2 md:row-span-2 min-h-[360px] md:min-h-[440px]";
    if (index === 1 || index === 2) return "md:col-span-1 md:row-span-1 min-h-[210px]";

    if (activeLayoutPattern === "merged-5") {
      if (index === 3) return "md:col-span-1 md:row-span-1 min-h-[240px]";
      if (index === 4) return "md:col-span-2 md:row-span-1 min-h-[240px]";
    } else {
      return "md:col-span-1 md:row-span-1 min-h-[230px]";
    }

    return "md:col-span-1 md:row-span-1 min-h-[220px]";
  };

  return (
    <section
      className={`pt-4 pb-6 sm:pb-10 md:pb-14 px-6 max-w-5xl mx-auto border-b text-left transition-colors ${
        isDarkMode ? "border-zinc-900" : "border-slate-200"
      }`}
    >
      

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 md:row-span-2 min-h-[380px] bg-zinc-900/60 rounded-3xl animate-pulse" />
          <div className="min-h-[190px] bg-zinc-900/60 rounded-3xl animate-pulse" />
          <div className="min-h-[190px] bg-zinc-900/60 rounded-3xl animate-pulse" />
          <div className="min-h-[220px] bg-zinc-900/60 rounded-3xl animate-pulse" />
          <div className="min-h-[220px] bg-zinc-900/60 rounded-3xl animate-pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {displayItems.map((post, index) => {
            const isLeadStory = index === 0;
            const isWideBottomStory = activeLayoutPattern === "merged-5" && index === 4;
            const headline = post.atomifiedTitle || post.title || "Press Dispatch";
            const summary = post.atomifiedText || post.text || "";

            return (
              <article
                key={post.id}
                onClick={() => navigate(`/feed/${post.id}`)}
                className={`group relative rounded-3xl overflow-hidden cursor-pointer shadow-lg transition-all duration-500 hover:shadow-2xl flex flex-col justify-end border ${
                  isDarkMode
                    ? "border-zinc-800/80 bg-zinc-950"
                    : "border-slate-200 bg-slate-900"
                } ${getTileClasses(index)}`}
              >
                {post.mediaUrl ? (
                  <img
                    src={post.mediaUrl}
                    alt={headline}
                    className="absolute inset-0 w-full h-full object-cover select-none transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                ) : (
                  <div
                    className={`absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105 ${
                      isLeadStory
                        ? "bg-gradient-to-br from-[#ff00cc]/25 via-zinc-900 to-black"
                        : "bg-gradient-to-br from-zinc-800 via-zinc-900 to-black"
                    }`}
                  />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-transparent pointer-events-none transition-opacity duration-300 group-hover:via-black/65" />

                <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                  <span
                    className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md backdrop-blur-md border ${
                      post.isSpotlight || post.isAdminAnnouncement
                        ? "bg-brand-pink text-white border-brand-pink/40"
                        : post.isBoosted
                        ? "bg-blue-600 text-white border-blue-400/40"
                        : "bg-black/75 text-zinc-300 border-white/10"
                    }`}
                  >
                    {post.isSpotlight || post.isAdminAnnouncement
                      ? "Editorial Spotlight"
                      : post.isBoosted
                      ? "Featured Promo"
                      : "Artist Wire"}
                  </span>
                </div>

                <div className="relative z-10 p-5 md:p-6 flex flex-col justify-end text-left">
                  <h3
                    className={`font-black uppercase tracking-tight text-white drop-shadow-md leading-tight transition-colors group-hover:text-brand-pink ${
                      isLeadStory
                        ? "text-xl sm:text-2xl md:text-3xl line-clamp-3"
                        : isWideBottomStory
                        ? "text-base sm:text-lg md:text-xl line-clamp-2"
                        : "text-sm sm:text-base line-clamp-2"
                    }`}
                  >
                    {headline}
                  </h3>

                  {summary && (
                    <p
                      className={`mt-2 text-xs md:text-sm text-zinc-300 drop-shadow font-medium leading-relaxed ${
                        isLeadStory || isWideBottomStory
                          ? "line-clamp-2 max-w-xl"
                          : "line-clamp-1"
                      }`}
                    >
                      {summary}
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-brand-pink transition-transform duration-300 group-hover:translate-x-1">
                    <span>Read Story</span>
                    <span>→</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default HomeNewsFeed;